import BigNumber from 'bignumber.js'
import { FetchingStatus } from 'config/constants'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { ButtonProps } from 'UIKit/Button/types'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import Text from 'UIKit/Text'
import { StyledActionButton } from '../../../components/styles'

type DepositButtonProps = Pick<ButtonProps, 'style'> & {
  handleApproval: () => void
  handleDeposit: () => void
  isApproved: boolean
  submiting: boolean
  disabled: boolean
  approveProcess: FetchingStatus
  account: string
  allowance: BigNumber
}

const DepositButton: React.FC<DepositButtonProps> = ({
  handleApproval,
  handleDeposit,
  isApproved,
  submiting,
  disabled,
  approveProcess,
  account,
  allowance,
  style,
}) => {
  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      <Flex width="100%">
        {(approveProcess === FetchingStatus.Fetched || !isApproved) && (
          <StyledActionButton
            width="100%"
            disabled={approveProcess === FetchingStatus.Fetching || isApproved}
            onClick={handleApproval}
            mr="12px"
          >
            {approveProcess === FetchingStatus.Fetching && (
              <Box mr="12px">
                <CircleLoader />
              </Box>
            )}

            <Text
              bold
              fontSize="14px"
              color={approveProcess === FetchingStatus.Fetching || isApproved ? 'textSubtle' : 'text'}
            >
              <Trans>Approve</Trans>
            </Text>
          </StyledActionButton>
        )}

        <StyledActionButton
          width="100%"
          disabled={!isApproved || submiting || disabled}
          onClick={handleDeposit}
          style={{ ...(style || {}) }}
        >
          {submiting && (
            <Box mr="12px">
              <CircleLoader />
            </Box>
          )}

          <Text bold fontSize="14px" color={disabled ? 'textSubtle' : 'text'}>
            <Trans>Deposit</Trans>
          </Text>
        </StyledActionButton>
      </Flex>

      {(approveProcess === FetchingStatus.Fetched || !isApproved) && (
        <StyledStepContainer>
          <StyledStepPoint className={account && allowance?.gt(0) ? 'completed' : 'active'}>
            {isApproved ? <Icons.SuccessIcon /> : '1'}
          </StyledStepPoint>
          <StyledStepBar className={account && allowance?.gt(0) && 'active'} />
          <StyledStepPoint className={account && allowance?.gt(0) && 'active'}>2</StyledStepPoint>
        </StyledStepContainer>
      )}
    </Flex>
  )
}

const StyledStepContainer = styled(Box)`
  margin-top: 12px;
  width: 60%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  margin-bottom: 24px;
`

const StyledStepBar = styled(Box)`
  width: 100%;
  height: 2px;
  flex: 1;
  background: linear-gradient(90deg, #e960af 0%, #e960af20 100%);

  &.active {
    background: linear-gradient(90deg, #06c270 0%, #f3c622 100%);
  }
`

const StyledStepPoint = styled(Box)`
  z-index: 1;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 15px;
  color: white;
  transition: 0.3;
  background: #e960af20;

  &.active {
    background: linear-gradient(270deg, #e960af -10.61%, #f3c622 112.88%);
    opacity: 1;
  }

  &.completed {
    background: transparent;
    border: 4px solid #06c270;
    opacity: 1;

    svg {
      min-width: 28px;
      fill: #06c270;
    }
  }
`

export default DepositButton
